import React from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  counterpartDocumentKind,
  getLegalDocumentComponent,
  legalDocumentPath,
  legalDocumentPathForUiLocale,
  legalDocumentTitle,
  legalLabelKey,
  LEGAL_DOCUMENT_PUBLISHED_ON,
  normalizeUiLocale,
  usesEnglishLegalFallback,
} from '../../legal/documents';
import type { LegalDocumentKind, LegalLocale } from '../../legal/documents';

/**
 * Styling for the published legal text. The document components are plain
 * semantic markup (`section` / `h2` / `p` / `table`), so the reading
 * typography lives here, scoped to the article, instead of on every element.
 */
const LEGAL_DOCUMENT_BODY_CLASS = [
  'mt-6 text-sm leading-relaxed text-foreground sm:text-base',
  'break-words',
  '[&>*:first-child]:mt-0',
  '[&_h2]:mt-10 [&_h2]:mb-3 [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:text-foreground sm:[&_h2]:text-xl',
  '[&_h3]:mt-6 [&_h3]:mb-2 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-foreground',
  '[&_h4]:mt-6 [&_h4]:mb-2 [&_h4]:text-sm [&_h4]:font-semibold [&_h4]:text-foreground',
  '[&_p]:my-4 [&_p]:leading-relaxed',
  '[&_strong]:font-semibold',
  '[&_em]:italic',
  '[&_a]:text-primary [&_a]:underline [&_a]:underline-offset-2 [&_a:hover]:no-underline',
  '[&_ul]:my-4 [&_ul]:list-disc [&_ul]:pl-6',
  '[&_ol]:my-4 [&_ol]:list-decimal [&_ol]:pl-6',
  '[&_li]:my-1 [&_li]:leading-relaxed',
  '[&_hr]:my-8 [&_hr]:border-border',
  '[&_blockquote]:my-6 [&_blockquote]:border-l-4 [&_blockquote]:border-border [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-muted-foreground',
  '[&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-xs',
  // Tables scroll inside the reading column on narrow screens instead of
  // pushing the page sideways.
  '[&_table]:my-6 [&_table]:block [&_table]:w-full [&_table]:overflow-x-auto',
  'sm:[&_table]:table sm:[&_table]:border-collapse',
  '[&_caption]:mb-2 [&_caption]:text-left [&_caption]:text-xs [&_caption]:text-muted-foreground',
  '[&_th]:border [&_th]:border-border [&_th]:bg-muted [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:align-top [&_th]:font-semibold',
  '[&_td]:border [&_td]:border-border [&_td]:px-3 [&_td]:py-2 [&_td]:align-top',
].join(' ');

interface LegalDocumentPageProps {
  kind: LegalDocumentKind;
  /** Language the document is published in, taken from the canonical URL. */
  documentLocale: LegalLocale;
}

export const LegalDocumentPage: React.FC<LegalDocumentPageProps> = ({ kind, documentLocale }) => {
  const { t, i18n } = useTranslation();
  const uiLocale = normalizeUiLocale(i18n?.resolvedLanguage || i18n?.language);
  const title = legalDocumentTitle(kind, documentLocale);
  const relatedKind = counterpartDocumentKind(kind);
  const showFallbackNotice = usesEnglishLegalFallback(uiLocale) && documentLocale === 'en';
  const Document = getLegalDocumentComponent(kind, documentLocale);

  return (
    <div className="mx-auto w-full max-w-3xl">
      <header>
        <p className="text-xs text-muted-foreground">
          {t('legal.last_updated')}:{' '}
          <time dateTime={LEGAL_DOCUMENT_PUBLISHED_ON}>{t('legal.last_updated_date')}</time>
        </p>
        <h1 className="mt-2 text-2xl font-bold tracking-tight text-foreground sm:text-3xl">{title}</h1>
      </header>

      {showFallbackNotice && (
        <p
          role="note"
          className="mt-6 rounded-md border border-border bg-muted px-4 py-3 text-xs text-muted-foreground"
        >
          {t('legal.fallback_notice')}
        </p>
      )}

      <article aria-label={title} className={LEGAL_DOCUMENT_BODY_CLASS}>
        <Document />
      </article>

      <nav aria-label={t('legal.related_documents')} className="mt-12 border-t border-border pt-6">
        <h2 className="text-sm font-semibold text-foreground">{t('legal.related_documents')}</h2>
        <a
          href={legalDocumentPath(relatedKind, documentLocale)}
          className="mt-2 inline-block text-sm font-medium text-primary underline underline-offset-2 hover:no-underline"
        >
          {t(legalLabelKey(relatedKind))}
        </a>
      </nav>
    </div>
  );
};

/**
 * Unprefixed legal URLs (`/termos`, `/privacidade`, `/terms`, `/privacy`) land
 * on the document for the visitor's interface language, so no legal link from
 * anywhere else in the product dead-ends.
 */
export const LegalDocumentRedirect: React.FC<{ kind: LegalDocumentKind }> = ({ kind }) => {
  const { i18n } = useTranslation();
  const uiLocale = normalizeUiLocale(i18n?.resolvedLanguage || i18n?.language);

  return <Navigate to={legalDocumentPathForUiLocale(kind, uiLocale)} replace />;
};
